import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import customerService from '../services/customerService';
import { CustomerMe, CustomerWallet } from '../types/customer';

/**
 * Customer (invitee) session, for the public booking page.
 *
 * SEPARATE FROM `AuthContext`, which is the HOST's session. A host viewing
 * their own booking page and a customer signing in to spend points are two
 * different identities that can be active in the same browser at the same time,
 * so they get separate contexts and separate storage keys.
 *
 * The wallet is refreshed on demand rather than polled: a balance changes only
 * when this person books or buys, and both of those already re-fetch.
 */

interface CustomerAuthValue {
  /** Null while signed out. */
  me: CustomerMe | null;
  loading: boolean;
  isSignedIn: boolean;
  /** The wallet for one organization, or null if they have none there. */
  walletFor: (organizationId: number | null | undefined) => CustomerWallet | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthValue | undefined>(undefined);

export const CustomerAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [me, setMe] = useState<CustomerMe | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!customerService.getCustomerToken()) {
      setMe(null);
      return;
    }
    setLoading(true);
    try {
      setMe(await customerService.getMe());
    } catch {
      // A rejected token means the session is over — expired, or the account was
      // reset elsewhere. Clear it rather than leaving a signed-in-looking header
      // that fails on every action.
      customerService.clearCustomerToken();
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Restore a session on mount if a token survived the last visit.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      await customerService.login(email, password);
      await refresh();
    },
    [refresh]
  );

  const signOut = useCallback(() => {
    customerService.logout();
    setMe(null);
  }, []);

  const walletFor = useCallback(
    (organizationId: number | null | undefined): CustomerWallet | null => {
      if (!me || !organizationId) return null;
      return me.wallets.find((w) => w.organizationId === organizationId) ?? null;
    },
    [me]
  );

  const value = useMemo(
    () => ({
      me,
      loading,
      /**
       * SIGNED IN MEANS "THE NEXT REQUEST WILL CARRY CREDENTIALS", not "we
       * once loaded a profile".
       *
       * Every authenticated call authenticates with the TOKEN, so deriving
       * this from `me` alone lets the two drift: a cleared token leaves a
       * screen that still believes it is signed in, sends a request with no
       * credentials, and shows a refusal the person cannot act on because the
       * form that would fix it is hidden from someone "signed in".
       */
      isSignedIn: me !== null && customerService.getCustomerToken() !== null,
      walletFor,
      signIn,
      signOut,
      refresh,
    }),
    [me, loading, walletFor, signIn, signOut, refresh]
  );

  return (
    <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = (): CustomerAuthValue => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) {
    throw new Error('useCustomerAuth must be used inside a CustomerAuthProvider');
  }
  return ctx;
};

export default CustomerAuthContext;
